import { useLazyQuery, useQuery } from "@apollo/client";
import { IonContent, IonPage } from "@ionic/react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useIntl } from "react-intl";
import { useHistory } from "react-router";

import Article from "@components/Article";
import ArticleHeader from "@components/ArticleHeader";
import HandleQuery from "@components/HandleQuery";
import styles from "@components/UnauthenticatedArticlePage.module.css";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import {
  ArticleShareOpenedDocument,
  ArticleShareOpenedMutation,
  ArticleShareOpenedMutationVariables,
  GetArticleDocument,
  GetArticleQuery,
  GetArticleQueryVariables,
  GetClientWithJourneysDocument,
  GetClientWithJourneysQuery,
  GetClientWithJourneysQueryVariables
} from "@typing/Generated";

type Props = {
  shareId: string;
};

const UnauthenticatedArticlePage = ({ shareId }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const history = useHistory();
  const [getJourneyFailed, setGotJourneyFailed] = useState<boolean>(false);

  const getArticleQuery = useQuery<GetArticleQuery, GetArticleQueryVariables>(GetArticleDocument, {
    variables: {
      articleShareId: shareId
    }
  });

  const [createArticleShareOpen] = useMutationWithValidation<
    ArticleShareOpenedMutation,
    ArticleShareOpenedMutationVariables
  >(ArticleShareOpenedDocument, "articleShare");

  useEffect(() => {
    if (getArticleQuery.data?.article.id) {
      createArticleShareOpen({
        articleShareId: shareId
      });
    }
  }, [createArticleShareOpen, getArticleQuery.data?.article.id, shareId]);

  const [getClientWithJourneys] = useLazyQuery<GetClientWithJourneysQuery, GetClientWithJourneysQueryVariables>(
    GetClientWithJourneysDocument
  );

  // If the user is logged in, forward them to the authed version of the article page. This randomly chooses
  // the first journey in the list... it would be nice if we could pick the most recent one but that seems
  // a little bit hard.
  useEffect(() => {
    if (myClient) {
      if (getArticleQuery.data) {
        getClientWithJourneys({ variables: { id: myClient.id } })
          .then(response => {
            if (response.data?.client.roles && response.data?.client.roles.length > 0) {
              const url = `/client/journeys/${response.data?.client.roles[0].journey.id}/articles/${getArticleQuery.data?.article.id}`;
              history.push(url);
            } else {
              setGotJourneyFailed(true);
            }
          })
          .catch(() => setGotJourneyFailed(true));
      }
    }
  }, [getArticleQuery.data, getClientWithJourneys, history, myClient]);

  // If there's a client, we intend to forward them to the journey version of this page, so hold off until that is
  // possible.
  if (myClient && !getJourneyFailed) {
    return null;
  }

  return (
    <IonPage className="ion-no-margin ion-no-padding">
      <Helmet>
        <meta content="noindex, nofollow" name="robots" />
        <title>{intl.formatMessage({ id: "pageTitles.unauthenticated.article.show" })}</title>
      </Helmet>
      <IonContent className="ion-no-margin ion-no-padding">
        <HandleQuery query={getArticleQuery}>
          {data => (
            <>
              <ArticleHeader />
              <div className={styles.pageContent}>
                <div className="ion-no-margin">
                  <Article article={data.article} journeyId={null} refetch={getArticleQuery.refetch} />
                </div>
              </div>
            </>
          )}
        </HandleQuery>
      </IonContent>
    </IonPage>
  );
};

export default UnauthenticatedArticlePage;
